import {
  Navigate,
  RouteObject,
  createBrowserRouter,
  defer,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import client from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath, SettingsTabsEnum } from '@/Config/app/router/index.path';
import { UserApi } from '@/Domain/api/User/User.api';
import Default from '@/Domain/components/App/Layout/DefaultLayout';
import { NotFound } from '@/Domain/components/App/NotFound';
import { Conversation } from '@/Domain/components/Inbox/Conversation/Conversation';
import { ArticleSheet } from '@/Domain/components/KnowledgeBase/ArticleSheet/Article.sheet';
import { AcceptInvitationPage } from '@/UI/AcceptInvitation/AcceptInvitation.page';
import { ActionsLoader } from '@/UI/Actions/Actions.loader';
import { GettingStartedPage } from '@/UI/GettingStarted/GettingStarted.page';
import { IdentityPage } from '@/UI/Identity/Identity.page';
import { InboxPage } from '@/UI/Inbox/Inbox.page';
import { InsightsPage } from '@/UI/Insights/Insights.page';
import IntegrationsPage from '@/UI/Integrations';
import { KnowledgeBasePage } from '@/UI/KnowledgeBase/KnowledgeBase.page';
import { SimulationPage } from '@/UI/Simulation/Simulation.page';
import { SettingsPage } from '@/UI/User/Settings/Settings.page';
import { WidgetSettingsPage } from '@/UI/Website/WidgetSettings.page';
import { WEBSITE_VIEW_QUERY_PARAM, WebsiteView } from '@/UI/Website/viewMode';

function RedirectConversationToInbox() {
  const { id } = useParams();
  const to = `../${IndexPath.inbox.root}/${id ?? ''}`;
  return <Navigate to={to} replace />;
}

function RedirectToSettings({ tab }: { tab: SettingsTabsEnum }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', tab);
  return <Navigate to={`../${IndexPath.settings}?${params.toString()}`} replace />;
}

export const routes: RouteObject[] = [
  {
    errorElement: <NotFound />,
    children: [
      {
        // Literal path beats the `:scenario` sibling below by route-match
        // scoring (static segments outrank dynamic params in v6), so the
        // order here is for readability, not correctness.
        path: IndexPath.acceptInvitation,
        element: <AcceptInvitationPage />,
      },
      {
        path: ':scenario',
        children: [
          {
            element: <Default />,
            children: [
              {
                index: true,
                element: <Navigate to={IndexPath.aiPreview} replace />,
              },
              {
                path: IndexPath.gettingStarted,
                element: <GettingStartedPage />,
                handle: { indexPath: IndexPath.gettingStarted },
              },
              {
                path: IndexPath.aiPreview,
                element: <div />,
                handle: { indexPath: IndexPath.aiPreview },
              },
              {
                path: IndexPath.knowledgeBase.root,
                element: <KnowledgeBasePage />,
                handle: { indexPath: IndexPath.knowledgeBase.root },
                children: [
                  {
                    // Nested route to open Article Sheet over Knowledge Base
                    path: 'articles/:id',
                    element: <ArticleSheet />,
                    handle: { indexPath: IndexPath.knowledgeBase.root },
                  },
                ],
              },
              {
                path: IndexPath.settings,
                element: <SettingsPage />,
                loader: () =>
                  defer({
                    settings: client.fetchQuery({ queryKey: ['settings'] }),
                    user: client.fetchQuery({
                      queryKey: [Endpoint.user.myself],
                      queryFn: () => UserApi.myself(),
                    }),
                  }),
                handle: { indexPath: IndexPath.settings },
              },
              {
                // Pre-SPA operator home — still arrives via bookmarks and
                // address-bar autocomplete.
                path: 'dashboard',
                element: <Navigate to=".." replace />,
              },
              {
                path: IndexPath.subscription,
                element: <RedirectToSettings tab={SettingsTabsEnum.Billing} />,
              },
              {
                path: IndexPath.user.accountSettings,
                element: <RedirectToSettings tab={SettingsTabsEnum.Organization} />,
              },
              {
                path: IndexPath.insights,
                element: <InsightsPage />,
                loader: () =>
                  defer({ settings: client.fetchQuery({ queryKey: [QueryKey.widgetSettings] }) }),
                handle: { indexPath: IndexPath.insights },
              },
              {
                path: IndexPath.identity,
                element: <IdentityPage />,
                loader: () =>
                  defer({
                    settings: client.fetchQuery({
                      queryKey: ['settings'],
                    }),
                  }),
                handle: { indexPath: IndexPath.identity },
              },
              {
                path: IndexPath.channels,
                element: <IntegrationsPage />,
                handle: { indexPath: IndexPath.channels },
              },
              {
                path: IndexPath.actions,
                element: <ActionsLoader />,
                handle: { indexPath: IndexPath.actions },
              },
              {
                path: IndexPath.simulation,
                element: <SimulationPage />,
                handle: { indexPath: IndexPath.simulation },
              },
              {
                path: IndexPath.website,
                element: <WidgetSettingsPage />,
                loader: () =>
                  defer({
                    widgetSettings: client.fetchQuery({
                      queryKey: [QueryKey.widgetSettings],
                    }),
                  }),
                handle: { indexPath: IndexPath.website },
              },
              {
                path: IndexPath.chatPage,
                element: (
                  <Navigate
                    to={`../${IndexPath.website}?${WEBSITE_VIEW_QUERY_PARAM}=${WebsiteView.ChatPage}`}
                    replace
                  />
                ),
              },
              {
                path: IndexPath.user.profile,
                element: <RedirectToSettings tab={SettingsTabsEnum.Account} />,
              },
            ],
          },
          {
            element: <Default withPadding={false} />,
            children: [
              {
                path: IndexPath.inbox.root,
                element: <InboxPage />,
                handle: {
                  indexPath: IndexPath.inbox.root,
                },
                children: [
                  {
                    path: ':id',
                    element: <Conversation />,
                    handle: {
                      indexPath: IndexPath.inbox.root,
                    },
                  },
                ],
              },
              {
                path: IndexPath.inboxBackwardCompatibilityConversation, // Backward compatibility with old links in integrations
                element: <RedirectConversationToInbox />,
              },
            ],
          },
        ],
      },
    ],
  },
];

export default createBrowserRouter(routes, {
  basename: '/i',
});
